import { useEffect, useMemo } from "react"
import { useSortedModels, type Model } from "resources/models"
import { usePortfolios, type Portfolio } from "resources/portfolios"
import { useModelsWithCoverage, type ModelWithCoverage } from "resources/model-coverage"
import { combineStatuses, ERROR, OK } from "helpers/data-statuses"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { getCoverageResourceFromInstrument } from "connected-components/dashboard/helpers"
import { useCoreActions } from "../../hooks/use-core-actions"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"

export default function ModelSelector({ disabled = false, onModelChange }: ModelSelectorProps) {
  const dashboardId = useDashboardId()
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const instrument = useAppSelector((s) => s.dashboard.core[dashboardId]?.instrument ?? null)
  const { setModelId } = useCoreActions()
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [models, modelsStatus] = useSortedModels()

  const coverageResource = useMemo(() => getCoverageResourceFromInstrument(instrument), [instrument])
  const portfolioLive = useMemo(
    () => (coverageResource?.resourceType === "portfolio"
      ? (portfolios as Portfolio[] | null)?.find(portfolio => portfolio.id === coverageResource.resourceId)?.live ?? null
      : null),
    [portfolios, coverageResource],
  )
  const { models: modelsWithCoverage, status, requiresResourceCoverage } = useModelsWithCoverage(
    models,
    modelsStatus,
    coverageResource?.resourceType ?? null,
    coverageResource?.resourceId ?? null,
    instrument,
    modelId,
    portfolioLive,
  )

  useEffect(
    () => {
      // Wait for the coverage gate itself (not just models/portfolios) so the default pick can
      // land on a covered model when coverage is knowable up front (etf/index/security) instead
      // of racing ahead of it — mergeModelsWithCoverage always treats whatever gets selected here
      // as covered, so picking before coverage resolves silently skips the gate this PR added.
      if (status === OK && portfoliosStatus === OK && !modelId && modelsWithCoverage) {
        const eligibleModels = requiresResourceCoverage
          ? (modelsWithCoverage as ModelWithCoverage[]).filter(model => model.hasCoverage)
          : modelsWithCoverage as Model[]
        const defaultModelId = getMostUsedOrDefaultModelId(portfolios as Portfolio[], eligibleModels)
        if (defaultModelId) {
          setModelId(defaultModelId)
        }
      }
    },
    [modelId, setModelId, portfolios, modelsWithCoverage, status, portfoliosStatus, requiresResourceCoverage],
  )

  const handleModelChange = onModelChange || setModelId

  const combinedStatus = useMemo(
    () => combineStatuses([status, portfoliosStatus]),
    [status, portfoliosStatus],
  )

  if (combinedStatus === ERROR) {
    return null
  }

  if (combinedStatus !== OK || !modelsWithCoverage) {
    return <div>Loading</div>
  }

  return <ModelSelectorAutocomplete
    models={modelsWithCoverage}
    value={modelId || ""}
    onChange={handleModelChange}
    disabled={disabled}
    requiresResourceCoverage={requiresResourceCoverage}
    resourceTypeLabel={coverageResource?.resourceType === "swap" ? "basket" : coverageResource?.resourceType}
    popperPlacement="bottom-end"
  />
}

interface ModelSelectorProps {
  disabled?: boolean
  onModelChange?: (modelId: string) => void
}
